import { supabase } from '../lib/supabase';

/**
 * PowerPoint master upload (wave 1).
 *
 * The browser checks the file (extension, size, ZIP magic bytes and a `[Content_Types].xml` that
 * declares PresentationML), computes its sha256 with SubtleCrypto, creates the template row with
 * status 'importing' (so the editor can open on it straight away), uploads the bytes unchanged to
 * the private Storage bucket `presentation-masters` at `<account_id>/<template_id>/<sha256>.pptx`
 * (or `.potx`), then creates the `template_import` ai_requests row and calls the
 * `import-presentation-template` edge function with the storage path. The worker downloads the
 * master, checks its sha256, keeps it, and fills the template.
 *
 * Progress shown to the user (see deriveImportStage):
 *   uploading -> uploaded -> reading -> suggesting -> ready, or failed (with a retry).
 * The upload itself runs here; this module keeps a small in-memory registry per template so the
 * progress view (and a retry) still sees it after the page navigates to the editor.
 */

/** Private bucket of the original masters (supabase/migrations/20261106000100_pptx_wave1.sql). */
export const PRESENTATION_MASTER_BUCKET = 'presentation-masters';

/** Largest master (the bucket's file_size_limit). */
export const MAX_PRESENTATION_BYTES = 50 * 1024 * 1024;

/** MIME types of the two accepted package kinds. */
export const PRESENTATION_MIME_TYPES = Object.freeze({
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  potx: 'application/vnd.openxmlformats-officedocument.presentationml.template',
});

/** `accept` attribute for file inputs. */
export const PRESENTATION_ACCEPT = `.pptx,.potx,${PRESENTATION_MIME_TYPES.pptx},${PRESENTATION_MIME_TYPES.potx}`;

/** An 'importing' template with no job and no change for this long is treated as failed. */
export const STALE_IMPORT_MS = 15 * 60 * 1000;

/**
 * An 'importing' row whose upload is not running in this tab (the tab that uploaded was closed
 * or reloaded) and has not changed for this long is treated as an abandoned upload.
 */
export const ABANDONED_UPLOAD_MS = 3 * 60 * 1000;

/** Largest `[Content_Types].xml` accepted in the browser check (real ones are a few KB). */
const MAX_CONTENT_TYPES_BYTES = 2 * 1024 * 1024;

/** Worker progress at which the model starts suggesting prompts (cloud-run-worker/presentationMaster.js). */
export const SUGGESTING_PROGRESS = 25;

/** Ordered visible steps. */
export const IMPORT_STEPS = Object.freeze([
  { key: 'uploaded', label: 'Uploaded' },
  { key: 'reading', label: 'Reading slides' },
  { key: 'suggesting', label: 'Suggesting prompts' },
  { key: 'ready', label: 'Ready' },
]);

/** Error with a machine code the UI can react to. */
export class PresentationImportError extends Error {
  /**
   * @param {string} message - User-facing message (no em dashes).
   * @param {string} [code] - INVALID_FILE | TOO_LARGE | NO_CRYPTO | UPLOAD_FAILED | TEMPLATE_LIMIT | USAGE_UNKNOWN | MASTER_MISSING | NEEDS_FILE | DISPATCH_FAILED | CREATE_FAILED.
   * @param {object} [details] - Extra facts (limit, used, status).
   */
  constructor(message, code = 'IMPORT_FAILED', details = {}) {
    super(message);
    this.name = 'PresentationImportError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Package extension of a file name, or null when it is not a PowerPoint package.
 * @param {string|null|undefined} name - File name.
 * @returns {'pptx'|'potx'|null}
 */
export function presentationExtension(name) {
  const match = /\.(pptx|potx)$/i.exec(String(name || ''));
  return match ? /** @type {'pptx'|'potx'} */ (match[1].toLowerCase()) : null;
}

/**
 * True when the file name is a .pptx or .potx.
 * @param {File|{name?: string}|null|undefined} file - File.
 * @returns {boolean}
 */
export function isPresentationFile(file) {
  return Boolean(file) && presentationExtension(file.name) !== null;
}

/**
 * Human size (MB with one decimal, KB under 1 MB).
 * @param {number} bytes - Bytes.
 * @returns {string}
 */
export function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/**
 * Template name from a file name.
 * @param {string} fileName - File name.
 * @returns {string}
 */
export function templateNameFromFile(fileName) {
  return String(fileName || '').replace(/\.(pptx|potx)$/i, '').trim() || 'Imported Presentation';
}

/**
 * Read a File/Blob as an ArrayBuffer (FileReader fallback for older environments).
 * @param {Blob} file - File.
 * @returns {Promise<ArrayBuffer>}
 */
export function readFileBytes(file) {
  if (file && typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(/** @type {ArrayBuffer} */ (reader.result));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file.'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * True when bytes start with a ZIP local file header (PK\x03\x04).
 * @param {Uint8Array} bytes - Leading bytes.
 * @returns {boolean}
 */
export function hasZipMagic(bytes) {
  return Boolean(bytes) && bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

/**
 * Read `[Content_Types].xml` from a ZIP package, or null when it has none / cannot be opened.
 * JSZip only inflates the one entry asked for, so a 50 MB deck is cheap to check.
 * @param {ArrayBuffer} buffer - Package bytes.
 * @returns {Promise<string|null>}
 */
export async function readContentTypes(buffer) {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(buffer);
    const entry = zip.file('[Content_Types].xml');
    if (!entry) return null;
    // The declared size comes from the ZIP directory; a huge part here is not a real deck.
    const declared = Number(entry._data && entry._data.uncompressedSize);
    if (Number.isFinite(declared) && declared > MAX_CONTENT_TYPES_BYTES) return null;
    return await entry.async('string');
  } catch {
    return null;
  }
}

/**
 * Check a chosen file before anything is created or uploaded.
 * @param {File|null|undefined} file - Chosen file.
 * @returns {Promise<{ ok: true, buffer: ArrayBuffer, extension: 'pptx'|'potx' } | { ok: false, code: string, error: string }>}
 */
export async function validatePresentationFile(file) {
  if (!file) return { ok: false, code: 'INVALID_FILE', error: 'Choose a PowerPoint file (.pptx or .potx).' };
  const extension = presentationExtension(file.name);
  if (!extension) return { ok: false, code: 'INVALID_FILE', error: 'Choose a PowerPoint file (.pptx or .potx).' };
  if (!file.size) return { ok: false, code: 'INVALID_FILE', error: 'This file is empty.' };
  if (file.size > MAX_PRESENTATION_BYTES) {
    return {
      ok: false,
      code: 'TOO_LARGE',
      error: `This file is ${formatFileSize(file.size)}. The limit is 50 MB. Compress the pictures in PowerPoint (File > Compress Pictures) or remove embedded videos, then try again.`,
    };
  }
  let buffer;
  try {
    buffer = await readFileBytes(file);
  } catch {
    return { ok: false, code: 'INVALID_FILE', error: 'This file could not be read. Try choosing it again.' };
  }
  if (!hasZipMagic(new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength)))) {
    return { ok: false, code: 'INVALID_FILE', error: 'This is not a PowerPoint file. Save it as .pptx or .potx in PowerPoint and try again.' };
  }
  const types = await readContentTypes(buffer);
  if (types === null) {
    return { ok: false, code: 'INVALID_FILE', error: 'This file is damaged or is not a PowerPoint file.' };
  }
  if (!/presentationml/i.test(types)) {
    return { ok: false, code: 'INVALID_FILE', error: 'This is an Office file but not a PowerPoint presentation.' };
  }
  return { ok: true, buffer, extension };
}

/**
 * Lowercase hex sha256 of bytes (SubtleCrypto; needs a secure context: https or localhost).
 * @param {ArrayBuffer} buffer - Bytes.
 * @returns {Promise<string>}
 */
export async function sha256Hex(buffer) {
  const subtle = typeof crypto !== 'undefined' && crypto ? crypto.subtle : null;
  if (!subtle) {
    throw new PresentationImportError('This browser cannot fingerprint the file. Open Quill over https and try again.', 'NO_CRYPTO');
  }
  const digest = await subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Storage path of a master.
 * @param {string} accountId - Owning account.
 * @param {string} templateId - Template.
 * @param {string} sha256 - Digest.
 * @param {'pptx'|'potx'} [extension] - Package kind.
 * @returns {string}
 */
export function masterStoragePath(accountId, templateId, sha256, extension = 'pptx') {
  return `${accountId}/${templateId}/${String(sha256).toLowerCase()}.${extension === 'potx' ? 'potx' : 'pptx'}`;
}

/**
 * Placeholder content while the template imports (templates.content is NOT NULL).
 * @param {string} name - Template name.
 * @param {string} fileName - Source file name.
 * @returns {string}
 */
export function importingPlaceholderContent(name, fileName) {
  return JSON.stringify({ template_type: 'powerpoint', template_name: name, sourceFileName: fileName, slides: [], variableCatalog: [] });
}

/**
 * True for Storage's "already exists" answer. Masters are content-addressed, so the same path
 * already holding an object means it holds these bytes.
 * @param {object|null|undefined} error - Storage error.
 * @returns {boolean}
 */
export function isAlreadyExistsError(error) {
  if (!error) return false;
  const code = String(error.statusCode ?? error.status ?? '');
  return code === '409' || /already exists|duplicate/i.test(`${error.message || ''} ${error.error || ''}`);
}

/**
 * Upload a master unchanged (no overwrite; an existing identical object counts as uploaded).
 * @param {object} client - Supabase client.
 * @param {string} path - Storage path.
 * @param {ArrayBuffer} buffer - Bytes.
 * @param {'pptx'|'potx'} extension - Package kind.
 * @returns {Promise<{ existed: boolean }>}
 */
export async function uploadMaster(client, path, buffer, extension) {
  const body = new Blob([buffer], { type: PRESENTATION_MIME_TYPES[extension] || PRESENTATION_MIME_TYPES.pptx });
  const { error } = await client.storage.from(PRESENTATION_MASTER_BUCKET).upload(path, body, {
    contentType: PRESENTATION_MIME_TYPES[extension] || PRESENTATION_MIME_TYPES.pptx,
    upsert: false,
    cacheControl: '3600',
  });
  if (error && !isAlreadyExistsError(error)) {
    const tooBig = /exceeded|too large|maximum allowed size|payload/i.test(error.message || '') || String(error.statusCode) === '413';
    throw new PresentationImportError(
      tooBig ? 'The file is larger than the upload limit (50 MB).' : `The upload failed: ${error.message || 'storage error'}. Check your connection and try again.`,
      tooBig ? 'TOO_LARGE' : 'UPLOAD_FAILED'
    );
  }
  return { existed: Boolean(error) };
}

/** One safe path segment (ids are uuids; never '/', '.' or '..'). */
const PATH_SEGMENT_RE = /^[A-Za-z0-9-]{1,64}$/;

/**
 * Remove every stored master of a deleted template (its folder `<account_id>/<template_id>/`,
 * which also holds earlier revisions). Call after the row was deleted; best effort, so a failure
 * only leaves an orphan (counted by the wave 1 verify script) and never blocks the delete.
 * @param {object} client - Supabase client.
 * @param {string} accountId - Account id.
 * @param {string} templateId - Template id.
 * @returns {Promise<{ removed: number }>}
 */
export async function removeTemplateMasters(client, accountId, templateId) {
  if (!PATH_SEGMENT_RE.test(String(accountId || '')) || !PATH_SEGMENT_RE.test(String(templateId || ''))) return { removed: 0 };
  try {
    const folder = `${accountId}/${templateId}`;
    const bucket = client.storage.from(PRESENTATION_MASTER_BUCKET);
    const { data, error } = await bucket.list(folder, { limit: 1000 });
    if (error || !Array.isArray(data) || data.length === 0) return { removed: 0 };
    const paths = data
      .map((object) => object && object.name)
      .filter((name) => typeof name === 'string' && /^[0-9a-f]{64}\.(pptx|potx)$/.test(name))
      .map((name) => `${folder}/${name}`);
    if (paths.length === 0) return { removed: 0 };
    const removal = await bucket.remove(paths);
    return { removed: removal?.error ? 0 : paths.length };
  } catch (err) {
    console.warn('Could not remove the stored PowerPoint file:', err?.message || err);
    return { removed: 0 };
  }
}

/**
 * Call the import edge function and turn an HTTP error into a PresentationImportError that keeps
 * the server's message and code (supabase-js hides the body in `error.context`).
 * @param {object} payload - Request body.
 * @param {{ client?: object }} [options] - Client override (tests).
 * @returns {Promise<object>} Response body.
 */
export async function invokeImportFunction(payload, { client = supabase } = {}) {
  const { data, error } = await client.functions.invoke('import-presentation-template', { body: payload });
  if (!error) return data;
  let body = data && typeof data === 'object' ? data : null;
  const response = error.context;
  if (!body && response && typeof response.json === 'function') {
    try {
      body = await (typeof response.clone === 'function' ? response.clone() : response).json();
    } catch {
      body = null;
    }
  }
  const message = (body && typeof body.error === 'string' && body.error) || error.message || 'Could not start the import.';
  const code = (body && typeof body.code === 'string' && body.code) || 'DISPATCH_FAILED';
  throw new PresentationImportError(message, code, { status: response?.status ?? null, limit: body?.limit, used: body?.used });
}

// ---------------------------------------------------------------------------
// Local registry (uploads in flight in this tab)
// ---------------------------------------------------------------------------

/** @type {Map<string, { stage: string, error: string|null, code: string|null, file: File|null, updatedAt: number }>} */
const localImports = new Map();
/** @type {Map<string, Set<Function>>} */
const localListeners = new Map();

/**
 * Local state of an import started in this tab, or null.
 * @param {string} templateId - Template id.
 * @returns {{ stage: string, error: string|null, code: string|null, file: File|null, updatedAt: number }|null}
 */
export function getLocalImport(templateId) {
  return localImports.get(templateId) || null;
}

/**
 * Subscribe to local state changes of one import.
 * @param {string} templateId - Template id.
 * @param {(state: object|null) => void} listener - Called on every change.
 * @returns {() => void} Unsubscribe.
 */
export function subscribeLocalImport(templateId, listener) {
  if (!localListeners.has(templateId)) localListeners.set(templateId, new Set());
  localListeners.get(templateId).add(listener);
  return () => localListeners.get(templateId)?.delete(listener);
}

/**
 * Update the local state of an import and notify listeners.
 * @param {string} templateId - Template id.
 * @param {object} patch - Fields.
 * @returns {void}
 */
function setLocalImport(templateId, patch) {
  const next = { stage: 'uploading', error: null, code: null, file: null, ...(localImports.get(templateId) || {}), ...patch, updatedAt: Date.now() };
  localImports.set(templateId, next);
  syncUnloadGuard();
  (localListeners.get(templateId) || []).forEach((fn) => {
    try {
      fn(next);
    } catch {
      // a listener's failure must not break the upload
    }
  });
}

/**
 * Forget local state (tests, or after the import finished).
 * @param {string} [templateId] - One template, or all when omitted.
 * @returns {void}
 */
export function clearLocalImports(templateId) {
  if (templateId) localImports.delete(templateId);
  else localImports.clear();
  syncUnloadGuard();
}

/**
 * beforeunload handler: the bytes are uploaded by this tab, so closing or reloading it while an
 * upload runs would stop the upload. Browsers show their own generic text.
 * @param {BeforeUnloadEvent} event - Event.
 * @returns {string|undefined}
 */
function warnBeforeUnload(event) {
  if (!hasUploadInProgress()) return undefined;
  event.preventDefault();
  // Chrome needs returnValue set; the text itself is not shown.
  event.returnValue = 'A PowerPoint file is still uploading.';
  return event.returnValue;
}

let unloadGuardOn = false;

/**
 * Register the beforeunload warning while any upload of this tab is running (it stays on across
 * in-app navigation, which does not stop the upload), and remove it afterwards.
 * @returns {void}
 */
function syncUnloadGuard() {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  const want = hasUploadInProgress();
  if (want && !unloadGuardOn) window.addEventListener('beforeunload', warnBeforeUnload);
  if (!want && unloadGuardOn) window.removeEventListener('beforeunload', warnBeforeUnload);
  unloadGuardOn = want;
}

// ---------------------------------------------------------------------------
// Stage derivation
// ---------------------------------------------------------------------------

/**
 * The visible stage of an import from the template row, its latest template_import request, and
 * this tab's local upload state.
 * @param {object} params - Inputs.
 * @param {object|null} params.template - templates row (status, generation_metadata, updated_at).
 * @param {object|null} [params.request] - Latest ai_requests row (status, progress, progress_message, error_message, updated_at).
 * @param {object|null} [params.local] - getLocalImport(templateId).
 * @param {number} [params.now] - Clock (ms).
 * @param {boolean} [params.loaded] - True once the template row was read (a null template then
 *   means it was removed, for example by a quota rejection, instead of "not created yet").
 * @returns {{ stage: 'uploading'|'uploaded'|'reading'|'suggesting'|'ready'|'failed', message: string, error: string|null, needsFile: boolean, code: string|null, removed: boolean }}
 *   `code` is the local failure code (TEMPLATE_LIMIT, UPLOAD_FAILED, ...) when known; `removed` is
 *   true when the template row no longer exists (nothing to retry).
 */
export function deriveImportStage({ template, request = null, local = null, now = Date.now(), loaded = false }) {
  const meta = template?.generation_metadata && typeof template.generation_metadata === 'object' ? template.generation_metadata : {};
  const importMeta = meta.import && typeof meta.import === 'object' ? meta.import : {};
  const hasStoredMaster = Boolean(template?.source_document?.path) || ['uploaded', 'queued'].includes(importMeta.state);
  const result = (fields) => ({ error: null, needsFile: false, code: null, removed: false, ...fields });
  const failed = (error, needsFile = false, code = null) =>
    result({ stage: 'failed', message: 'Import failed', error: error || 'The import failed.', needsFile: needsFile && !local?.file, code: code || null });

  if (!template) {
    // Checked before "not created yet": a quota rejection deletes the placeholder row, and the
    // reason only exists in this tab's local state.
    if (local?.stage === 'failed') return { ...failed(local.error, false, local.code), removed: true };
    if (loaded) return { ...failed('This template no longer exists. It may have been deleted.'), removed: true };
    return result({ stage: 'uploading', message: 'Preparing the import...' });
  }
  const status = template.status || 'ready';
  if (status !== 'importing' && status !== 'failed') return result({ stage: 'ready', message: 'Ready' });
  if (status === 'failed') {
    const reason = meta.error || (request && ['failed', 'cancelled'].includes(request.status) ? request.error_message : null);
    return failed(reason, !hasStoredMaster, local?.stage === 'failed' ? local.code : null);
  }
  if (local?.stage === 'failed') return failed(local.error, local.code === 'NEEDS_FILE' || !hasStoredMaster, local.code);

  if (request) {
    if (request.status === 'failed') return failed(request.error_message, !hasStoredMaster);
    if (request.status === 'cancelled') return failed('The import was cancelled.', !hasStoredMaster);
    if (request.status === 'completed') return result({ stage: 'ready', message: 'Ready' });
    if (request.status === 'processing') {
      const progress = Number(request.progress) || 0;
      if (progress >= SUGGESTING_PROGRESS) {
        return result({ stage: 'suggesting', message: request.progress_message || 'Suggesting prompts...' });
      }
      return result({ stage: 'reading', message: 'Reading slides...' });
    }
    return result({ stage: 'uploaded', message: 'Uploaded. Waiting to read the slides...' });
  }

  if (local?.stage === 'uploaded') return result({ stage: 'uploaded', message: 'Uploaded. Starting the import...' });
  if (local?.stage === 'uploading') return result({ stage: 'uploading', message: 'Uploading the file...' });

  const lastChange = Date.parse(template.updated_at || importMeta.queuedAt || importMeta.startedAt || '') || 0;
  if (lastChange && now - lastChange > STALE_IMPORT_MS) {
    return failed(
      hasStoredMaster ? 'The import stopped before it finished.' : 'The upload did not finish. Choose the file again to retry.',
      !hasStoredMaster
    );
  }
  if (hasStoredMaster) return result({ stage: 'uploaded', message: 'Uploaded. Waiting to read the slides...' });
  // The bytes are uploaded by the tab that started the import. When that tab is gone (closed or
  // reloaded) nothing will finish the upload, so say so after a short wait instead of 15 minutes.
  if (lastChange && now - lastChange > ABANDONED_UPLOAD_MS) {
    return failed('The upload did not finish. If the page that was uploading it was closed or reloaded, choose the file again to retry.', true);
  }
  return result({ stage: 'uploading', message: 'Uploading the file...' });
}

/**
 * True while this tab is uploading the bytes of an import (closing the tab would stop it).
 * @param {string|null|undefined} [templateId] - One template, or any import when omitted.
 * @returns {boolean}
 */
export function hasUploadInProgress(templateId) {
  if (templateId) return getLocalImport(templateId)?.stage === 'uploading';
  for (const state of localImports.values()) if (state.stage === 'uploading') return true;
  return false;
}

// ---------------------------------------------------------------------------
// Orchestration
// ---------------------------------------------------------------------------

/**
 * Patch a template row (best effort; RLS scopes it to the caller's account).
 * @param {object} client - Supabase client.
 * @param {string} templateId - Template id.
 * @param {string} accountId - Account id.
 * @param {object} patch - Columns.
 * @returns {Promise<void>}
 */
async function patchTemplate(client, templateId, accountId, patch) {
  try {
    await client.from('templates').update(patch).eq('id', templateId).eq('account_id', accountId);
  } catch (error) {
    console.warn('Could not update the importing template:', error?.message || error);
  }
}

/**
 * Upload the bytes (when given) and queue the server import for an existing 'importing' template.
 * @param {object} params - Parameters.
 * @returns {Promise<{ requestId: string }>}
 */
async function uploadAndDispatch({ client, invoke, createRequest, template, accountId, fileName, description, upload, pointer, file }) {
  const templateId = template.id;
  let master = pointer;
  if (upload) {
    setLocalImport(templateId, { stage: 'uploading', error: null, code: null, file });
    const path = masterStoragePath(accountId, templateId, upload.sha256, upload.extension);
    await uploadMaster(client, path, upload.buffer, upload.extension);
    master = { storagePath: path, sha256: upload.sha256, bytes: upload.buffer.byteLength, fileName };
    await patchTemplate(client, templateId, accountId, {
      generation_metadata: {
        import: { state: 'uploaded', fileName, bytes: master.bytes, sha256: master.sha256, storagePath: path, startedAt: upload.startedAt },
      },
    });
  }
  setLocalImport(templateId, { stage: 'uploaded', error: null, code: null });

  const request = await createRequest({
    type: 'template_import',
    title: `Importing ${fileName}`,
    templateId,
    data: { fileName, accountId, templateId },
  });
  if (!request?.id) throw new PresentationImportError('Could not create the import job.', 'DISPATCH_FAILED');

  try {
    await invoke({
      requestId: request.id,
      templateId,
      storagePath: master.storagePath,
      sha256: master.sha256,
      fileName: master.fileName || fileName,
      bytes: master.bytes,
      accountId,
      ...(description ? { description } : {}),
    });
  } catch (error) {
    const wrapped = error instanceof PresentationImportError ? error : new PresentationImportError(error?.message || 'Could not start the import.', 'DISPATCH_FAILED');
    wrapped.details = { ...(wrapped.details || {}), requestId: request.id };
    throw wrapped;
  }
  return { requestId: request.id };
}

/**
 * Record a failure locally and on the template row (so the editor shows Failed + Retry). A quota
 * rejection of a NEW import (removeOnLimit) removes the placeholder row and the stored object
 * instead, since nothing was imported and the row would only use up the quota.
 * @param {object} params - Parameters.
 * @returns {Promise<void>}
 */
async function recordFailure({ client, template, accountId, error, deleteTemplate, storagePath, removeOnLimit = false }) {
  const code = error?.code || 'IMPORT_FAILED';
  const message = error?.message || 'The import failed.';
  const requestId = error?.details?.requestId || null;
  if (requestId && code === 'DISPATCH_FAILED') {
    // The edge call failed in the browser (for example the connection dropped while the answer
    // was on its way). The server may still have queued the job: ask the request row first.
    const outcome = await reconcileDispatch(client, requestId, message);
    if (outcome === 'queued') {
      setLocalImport(template.id, { stage: 'uploaded', error: null, code: null });
      return;
    }
  }
  if (code === 'TEMPLATE_LIMIT' && removeOnLimit) {
    try {
      if (storagePath) await client.storage.from(PRESENTATION_MASTER_BUCKET).remove([storagePath]);
    } catch {
      // the object is content-addressed and harmless if it stays
    }
    if (typeof deleteTemplate === 'function') await deleteTemplate(template.id);
    setLocalImport(template.id, { stage: 'failed', error: message, code, file: null });
    return;
  }
  setLocalImport(template.id, { stage: 'failed', error: message, code });
  await markTemplateFailedIfImporting(client, template, accountId, { message, storagePath });
}

/**
 * After a browser-side edge-call failure, find out whether the server queued the job anyway.
 * A job spec on a request that has not failed means the server accepted it (the worker, the
 * dispatch failure hook or the stuck-job sweeper owns the outcome from there). A request the
 * server never queued is failed here, so a late server answer cannot queue it afterwards.
 * @param {object} client - Supabase client.
 * @param {string} requestId - ai_requests id.
 * @param {string} message - Failure message for a request that was never queued.
 * @returns {Promise<'queued'|'failed'|'unknown'>}
 */
async function reconcileDispatch(client, requestId, message) {
  try {
    const { data, error } = await client.from('ai_requests').select('id, status, request_data').eq('id', requestId).maybeSingle();
    if (error || !data) return 'unknown';
    const queued = Boolean(data.request_data && typeof data.request_data === 'object' && data.request_data.job);
    if (['processing', 'completed'].includes(data.status)) return 'queued';
    if (['pending', 'queued'].includes(data.status) && queued) return 'queued';
    if (['pending', 'queued'].includes(data.status)) {
      await client
        .from('ai_requests')
        .update({ status: 'failed', error_message: message, completed_at: new Date().toISOString() })
        .eq('id', requestId)
        .in('status', ['pending', 'queued']);
    }
    return 'failed';
  } catch {
    return 'unknown';
  }
}

/**
 * Mark the template failed, only while it is still 'importing', merging the reason into its
 * current generation_metadata (never a stale copy held by this tab), so a worker that already
 * finished (status 'ready', its own metadata) is never overwritten.
 * @param {object} client - Supabase client.
 * @param {object} template - Template (id; generation_metadata used when the row cannot be read).
 * @param {string} accountId - Account id.
 * @param {{ message: string, storagePath?: string|null }} failure - Reason and master path.
 * @returns {Promise<void>}
 */
async function markTemplateFailedIfImporting(client, template, accountId, { message, storagePath = null }) {
  try {
    const { data: current, error } = await client
      .from('templates')
      .select('id, status, generation_metadata')
      .eq('id', template.id)
      .eq('account_id', accountId)
      .maybeSingle();
    if (error || !current || current.status !== 'importing') return;
    const meta = current.generation_metadata && typeof current.generation_metadata === 'object' ? current.generation_metadata : {};
    await client
      .from('templates')
      .update({
        status: 'failed',
        generation_metadata: {
          ...meta,
          ...(storagePath ? { import: { ...(meta.import || {}), storagePath } } : {}),
          error: message,
          failedAt: new Date().toISOString(),
        },
      })
      .eq('id', template.id)
      .eq('account_id', accountId)
      .eq('status', 'importing');
  } catch (err) {
    console.warn('Could not mark the import failed:', err?.message || err);
  }
}

/**
 * Start a PowerPoint import: check the file, create the 'importing' template row, report it
 * through `onCreated` (open the editor there), then upload and queue the server import.
 * @param {object} params - Parameters.
 * @param {File} params.file - Chosen .pptx/.potx.
 * @param {string} params.accountId - Active account.
 * @param {string} [params.name] - Template name (defaults to the file name).
 * @param {string} [params.description] - Template description.
 * @param {(row: object) => Promise<{ success: boolean, data?: object, error?: string }>} params.createTemplate - AppContext actions.createTemplate.
 * @param {(req: object) => Promise<object>} params.createRequest - AIRequestContext createRequest.
 * @param {(id: string) => Promise<unknown>} [params.deleteTemplate] - AppContext actions.deleteTemplate (quota cleanup).
 * @param {(template: object) => void} [params.onCreated] - Called once the row exists (before the upload).
 * @param {object} [params.client] - Supabase client (tests).
 * @param {Function} [params.invoke] - Edge call (tests).
 * @returns {Promise<{ template: object, done: Promise<{ requestId: string|null, error: PresentationImportError|null }> }>}
 *   Rejects only when nothing was created (invalid file, no crypto, row insert failed). Upload and
 *   dispatch failures resolve `done` with the error and mark the template failed.
 */
export async function startPresentationImport({
  file,
  accountId,
  name,
  description,
  createTemplate,
  createRequest,
  deleteTemplate,
  onCreated,
  client = supabase,
  invoke = (payload) => invokeImportFunction(payload, { client }),
}) {
  if (!accountId) throw new PresentationImportError('No active workspace. Choose a workspace and try again.', 'CREATE_FAILED');
  const checked = await validatePresentationFile(file);
  if (!checked.ok) throw new PresentationImportError(checked.error, checked.code);
  const sha256 = await sha256Hex(checked.buffer);
  const startedAt = new Date().toISOString();
  const templateName = (name && String(name).trim()) || templateNameFromFile(file.name);
  const storagePathFor = (id) => masterStoragePath(accountId, id, sha256, checked.extension);

  const created = await createTemplate({
    name: templateName,
    description: (description && String(description).trim()) || 'Imported from PowerPoint',
    content: importingPlaceholderContent(templateName, file.name),
    template_type: 'powerpoint',
    status: 'importing',
    generation_metadata: { import: { state: 'uploading', fileName: file.name, bytes: file.size, sha256, startedAt } },
  });
  if (!created?.success || !created.data?.id) {
    throw new PresentationImportError(created?.error || 'Could not create the template.', 'CREATE_FAILED');
  }
  const template = { ...created.data, generation_metadata: { import: { state: 'uploading', fileName: file.name, bytes: file.size, sha256, startedAt } } };
  setLocalImport(template.id, { stage: 'uploading', error: null, code: null, file });
  if (typeof onCreated === 'function') onCreated(created.data);

  const done = uploadAndDispatch({
    client,
    invoke,
    createRequest,
    template,
    accountId,
    fileName: file.name,
    description: description && String(description).trim(),
    upload: { buffer: checked.buffer, sha256, extension: checked.extension, startedAt },
    file,
  }).then(
    (result) => ({ requestId: result.requestId, error: null }),
    async (error) => {
      const wrapped = error instanceof PresentationImportError ? error : new PresentationImportError(error?.message || 'The import failed.', 'DISPATCH_FAILED');
      await recordFailure({ client, template, accountId, error: wrapped, deleteTemplate, storagePath: storagePathFor(template.id), removeOnLimit: true });
      return { requestId: null, error: wrapped };
    }
  );
  return { template: created.data, done };
}

/**
 * Retry a failed (or stuck) import of an existing template. Uses the master already in Storage
 * when there is one; otherwise uploads `file` (the one still held by this tab, or a newly chosen
 * one). Throws NEEDS_FILE when neither exists.
 * @param {object} params - Parameters.
 * @param {object} params.template - templates row (id, source_document, generation_metadata, description).
 * @param {string} params.accountId - Active account.
 * @param {File} [params.file] - File to upload (a new choice).
 * @param {(req: object) => Promise<object>} params.createRequest - AIRequestContext createRequest.
 * @param {(id: string) => Promise<unknown>} [params.deleteTemplate] - Quota cleanup.
 * @param {object} [params.client] - Supabase client (tests).
 * @param {Function} [params.invoke] - Edge call (tests).
 * @returns {Promise<{ requestId: string|null, error: PresentationImportError|null }>}
 */
export async function retryPresentationImport({
  template,
  accountId,
  file,
  createRequest,
  deleteTemplate,
  client = supabase,
  invoke = (payload) => invokeImportFunction(payload, { client }),
}) {
  if (!template?.id) throw new PresentationImportError('The template could not be found.', 'CREATE_FAILED');
  const meta = template.generation_metadata && typeof template.generation_metadata === 'object' ? template.generation_metadata : {};
  const importMeta = meta.import && typeof meta.import === 'object' ? meta.import : {};
  const source = template.source_document && template.source_document.bucket === PRESENTATION_MASTER_BUCKET ? template.source_document : null;
  const chosen = file || getLocalImport(template.id)?.file || null;

  const pendingPointer = ['uploaded', 'queued'].includes(importMeta.state) && importMeta.storagePath ? importMeta : null;

  let pointer = null;
  let upload = null;
  let fileName;
  if (!file && source) {
    pointer = { storagePath: source.path, sha256: source.sha256, bytes: source.bytes, fileName: source.fileName };
    fileName = source.fileName;
  } else if (!file && pendingPointer) {
    pointer = { storagePath: pendingPointer.storagePath, sha256: pendingPointer.sha256, bytes: pendingPointer.bytes, fileName: pendingPointer.fileName };
    fileName = pendingPointer.fileName;
  } else if (chosen) {
    const checked = await validatePresentationFile(chosen);
    if (!checked.ok) throw new PresentationImportError(checked.error, checked.code);
    upload = { buffer: checked.buffer, sha256: await sha256Hex(checked.buffer), extension: checked.extension, startedAt: new Date().toISOString() };
    fileName = chosen.name;
  } else {
    throw new PresentationImportError('Choose the PowerPoint file again to retry.', 'NEEDS_FILE');
  }
  fileName = fileName || 'presentation.pptx';

  await patchTemplate(client, template.id, accountId, {
    status: 'importing',
    generation_metadata: { import: { ...importMeta, state: upload ? 'uploading' : importMeta.state || 'uploaded', retriedAt: new Date().toISOString() } },
  });
  setLocalImport(template.id, { stage: upload ? 'uploading' : 'uploaded', error: null, code: null, ...(chosen ? { file: chosen } : {}) });
  try {
    const result = await uploadAndDispatch({
      client,
      invoke,
      createRequest,
      template: { ...template, generation_metadata: { import: importMeta } },
      accountId,
      fileName,
      description: template.description,
      upload,
      pointer,
      file: chosen,
    });
    return { requestId: result.requestId, error: null };
  } catch (error) {
    const wrapped = error instanceof PresentationImportError ? error : new PresentationImportError(error?.message || 'The import failed.', 'DISPATCH_FAILED');
    await recordFailure({
      client,
      template: { ...template, generation_metadata: { ...meta, import: importMeta } },
      accountId,
      error: wrapped,
      deleteTemplate,
      storagePath: upload ? masterStoragePath(accountId, template.id, upload.sha256, upload.extension) : null,
    });
    return { requestId: null, error: wrapped };
  }
}

/**
 * Load the import facts of one template and its latest import request (for the progress view).
 * @param {string} templateId - Template id.
 * @param {{ client?: object }} [options] - Client override (tests).
 * @returns {Promise<{ template: object|null, request: object|null }>}
 */
export async function fetchImportStatus(templateId, { client = supabase } = {}) {
  const [templateResult, requestResult] = await Promise.all([
    client
      .from('templates')
      .select('id, name, description, status, source_document, generation_metadata, updated_at, template_type')
      .eq('id', templateId)
      .maybeSingle(),
    client
      .from('ai_requests')
      .select('id, status, progress, progress_message, error_message, updated_at, created_at')
      .eq('template_id', templateId)
      .eq('type', 'template_import')
      .order('created_at', { ascending: false })
      .limit(1),
  ]);
  if (templateResult.error) throw templateResult.error;
  const rows = Array.isArray(requestResult.data) ? requestResult.data : [];
  return { template: templateResult.data || null, request: rows[0] || null };
}
